import { useTranslate } from "react-admin";

import addMinutes from "date-fns/addMinutes";
import EnumRiskLevel from "@wallet-manager/node-types/dist/types/postgres/const/RiskLevel";

import { Button } from "../MuiGenerals";
import { RESCAN_LOCK_TIME_MS } from "../../utils/constant";
import { sortObjectByKey, toDisplayTime } from "../../utils/helper";
import { useZusDialogStore } from "../../zustand/store";

import InfoBanner from "./InfoBanner";
import RiskLevelBadge from "./RiskLevelBadge";

interface RescanApplyProps {
  onApply: () => void;
}

// Second step in the rescan flow. Shown after Merkle Science has returned
// a result. Asks the user to choose between applying the new level or
// discarding the rescan.
const RescanApply = ({ onApply }: RescanApplyProps) => {
  const translate = useTranslate();
  const tEnum = (key: string) => translate(`enumConstants.${key}`);
  const t = (key: string, options?: Record<string, any>) =>
    options
      ? translate(`walletManagement.${key}`, options)
      : translate(`walletManagement.${key}`);
  const zusDialog = useZusDialogStore();
  const handleClose = () => zusDialog.close();

  const {
    display_name,
    wallet_address,
    current_risk_level,
    current_risk_level_label,
    returned_risk_level,
    rescan_time,
    triggered_by,
  } = zusDialog.meta || {};

  const isNoData =
    Number(returned_risk_level) === EnumRiskLevel.NoData ||
    Number(returned_risk_level) === EnumRiskLevel.NoAssessment;

  // Compose "current risk text" for the bottom reminder, e.g. "5 Critical".
  // Prefer the label set by the orchestrator; fall back to the same translated
  // enum lookup the badge uses so the text never reads "Risk Level: 5".
  const numericCurrentRisk = Number(current_risk_level);
  const currentRiskText =
    current_risk_level_label ||
    (EnumRiskLevel[numericCurrentRisk]
      ? tEnum(EnumRiskLevel[numericCurrentRisk])
      : String(current_risk_level));

  const numericReturnedRisk = Number(returned_risk_level);
  const returnedRiskText = EnumRiskLevel[numericReturnedRisk]
    ? tEnum(EnumRiskLevel[numericReturnedRisk])
    : String(returned_risk_level);

  // Prefer the API-provided lock duration, fall back to the compile-time
  // constant when the API hasn't responded yet.
  const lockDuration =
    (zusDialog.meta?.lock_time_ms as number | undefined) ?? RESCAN_LOCK_TIME_MS;

  // Compute the next time the wallet can be rescanned from the shared lock duration.
  const nextRescanTime = rescan_time
    ? toDisplayTime(
        addMinutes(new Date(rescan_time), lockDuration / (60 * 1000)),
      )
    : "";

  // `created_date` comes from the backend as an ISO string ("2026-09-25T03:52:02.302479Z");
  // render it in the project's display format ("2026-09-25 03:52:02") so it
  // matches every other timestamp on the page.
  const displayRescanTime = rescan_time ? toDisplayTime(rescan_time) : "";

  const handleViewResponse = () => {
    const { raw_response } = zusDialog.meta || {};
    const sortedRawResponse = raw_response
      ? sortObjectByKey(raw_response)
      : null;
    zusDialog.openExtra("providerResponseDialog", {
      ...zusDialog.meta,
      raw_response: sortedRawResponse,
    });
  };

  return (
    <>
      <div style={{ padding: "12px 24px 12px" }}>
        <div style={{ marginBottom: "12px" }}>
          <InfoBanner tone={isNoData ? "warning" : "success"}>
            {isNoData
              ? t("rescan_no_data_message")
              : t("rescan_completed_message")}
          </InfoBanner>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>{t("display_name")}</div>
          <div style={valueStyle}>{display_name}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("walletAddress")}</div>
          <div style={valueStyle}>{wallet_address}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("current_arrow_returned")}</div>
          <div
            style={{
              ...valueStyle,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span
              style={{
                textDecoration: "line-through",
                color: "#8C8C8C",
              }}
            >
              <RiskLevelBadge riskLevel={current_risk_level} />
            </span>
            <span>→</span>
            <RiskLevelBadge riskLevel={returned_risk_level} />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("rescan_time")}</div>
          <div style={valueStyle}>{displayRescanTime}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("rescan_triggered_by")}</div>
          <div style={valueStyle}>{triggered_by}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("provider_response")}</div>
          <div style={valueStyle}>
            <Button
              variant="text"
              color="secondary"
              onClick={handleViewResponse}
              sx={{
                textTransform: "none",
                padding: 0,
                minWidth: 0,
                textDecoration: "underline",
              }}
            >
              {t("view_response")}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 24px 16px" }}>
        <InfoBanner tone="info">
          {isNoData
            ? t("rescan_no_data_explanation", {
                currentRiskText,
                returnedRiskText,
              })
            : t("rescan_keep_until_apply", {
                currentRiskText,
                nextRescanTime,
              })}
        </InfoBanner>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "8px 24px 20px",
        }}
      >
        <Button color="secondary" variant="contained" onClick={onApply}>
          {t("apply")}
        </Button>
        <Button
          sx={{
            backgroundColor: "#9CA3AF !important",
            color: "#FFFFFF !important",
            "&:hover": { backgroundColor: "#7F8B9C !important" },
          }}
          variant="contained"
          onClick={handleClose}
        >
          {translate("common.cancel")}
        </Button>
      </div>
    </>
  );
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  gap: "12px",
  padding: "6px 0",
  alignItems: "center",
};

const labelStyle: React.CSSProperties = {
  color: "#8C8C8C",
  fontSize: "13px",
};

const valueStyle: React.CSSProperties = {
  color: "#262626",
  fontSize: "13px",
  wordBreak: "break-all",
};

export default RescanApply;
